import * as m from '@staiter/i18n';
import type { LandedCostBasis } from '@staiter/shared';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Controller, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { PageHeader } from '~/components/data';
import { StockEntryPicker } from '~/components/domain';
import { useToast } from '~/components/feedback';
import { Button, DateTimePicker, Input, Select } from '~/components/primitives';
import { applyValidationErrors } from '~/lib/api/apply-validation-errors';
import { createLandedCost } from '~/lib/api/landed-cost';
import { useZodForm } from '~/lib/forms/useZodForm';
import shared from './_auth._shared.module.css';

export const Route = createFileRoute('/_auth/landed-cost/new')({ component: NewLandedCost });

const Currency = z.enum(['KZT', 'USD', 'EUR', 'RUB', 'CNY']);
const Basis = z.enum(['by_qty', 'by_amount', 'by_weight']);

const ChargeSchema = z.object({
  title: z.string().min(1, m.route_lc_new_err_title_required()),
  amount: z
    .string()
    .min(1, m.route_lc_new_err_amount_required())
    .regex(/^\d+(\.\d+)?$/, m.route_lc_new_err_amount_format())
    .refine((v) => Number(v) > 0, m.route_lc_new_err_amount_gt_zero()),
  currency: Currency,
  basis: Basis,
});

const SourceEntrySchema = z.object({
  id: z.string().min(1, m.route_lc_new_err_source_id_required()),
});

const FormSchema = z.object({
  posting_at: z.string().min(1),
  currency: Currency,
  source_entries: z.array(SourceEntrySchema).min(1, m.route_lc_new_err_source_min()),
  charges: z.array(ChargeSchema).min(1, m.route_lc_new_err_charges_min()),
  notes: z.string().default(''),
});
type FormValues = z.infer<typeof FormSchema>;

function NewLandedCost(): JSX.Element {
  const nav = useNavigate();
  const toast = useToast();

  const form = useZodForm(FormSchema, {
    posting_at: new Date().toISOString().slice(0, 16),
    currency: 'KZT',
    source_entries: [{ id: '' }],
    charges: [{ title: '', amount: '0', currency: 'KZT', basis: 'by_amount' }],
    notes: '',
  });
  const { control, handleSubmit, register, formState } = form;
  const { errors, isValid } = formState;

  const sourceArr = useFieldArray({ control, name: 'source_entries' });
  const chargesArr = useFieldArray({ control, name: 'charges' });

  const createMut = useMutation({
    mutationFn: (data: FormValues) =>
      createLandedCost({
        posting_at: new Date(data.posting_at).toISOString(),
        currency: data.currency,
        source_entry_ids: data.source_entries.map((s) => s.id).filter((s) => s.trim().length > 0),
        charges: data.charges.map((c) => ({
          title: c.title,
          amount: c.amount,
          currency: c.currency,
          basis: c.basis,
        })),
        ...(data.notes.trim() ? { notes: data.notes.trim() } : {}),
      }),
    onSuccess: (lc) => {
      toast.push({ variant: 'success', title: m.route_lc_new_toast_created() });
      nav({ to: '/landed-cost/$id', params: { id: lc.id } });
    },
    onError: (err: Error) => {
      if (applyValidationErrors(form, err)) return;
      toast.push({
        variant: 'error',
        title: m.route_lc_new_toast_failed(),
        description: err.message,
      });
    },
  });

  const onSubmit = (data: FormValues): void => createMut.mutate(data);

  return (
    <form className={shared.page} onSubmit={handleSubmit(onSubmit)}>
      <PageHeader title={m.route_lc_new_title()} description={m.route_lc_new_description()} />

      {/* FE-120 Sprint Q+: empty-state guidance helper for first-time users. */}
      <div
        style={{
          padding: 'var(--space-3)',
          background: 'var(--info-bg-15)',
          borderLeft: '3px solid var(--info)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--fs-13, 13px)',
          color: 'var(--color-text-secondary)',
          marginBottom: 'var(--space-4)',
        }}
      >
        {m.lc_new_guidance()}
      </div>

      <div className={shared.filters}>
        <Controller
          control={control}
          name="posting_at"
          render={({ field, fieldState }) => (
            <DateTimePicker
              label={m.route_lc_new_posting_at_label()}
              value={field.value || null}
              onChange={(v) => field.onChange(v ?? '')}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="currency"
          render={({ field }) => (
            <Select
              label={m.route_lc_new_currency_label()}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              options={[
                { value: 'KZT', label: 'KZT' },
                { value: 'USD', label: 'USD' },
                { value: 'EUR', label: 'EUR' },
                { value: 'RUB', label: 'RUB' },
                { value: 'CNY', label: 'CNY' },
              ]}
            />
          )}
        />
      </div>

      <section>
        {/* FE-119 Sprint Q+: section heading с count badge. */}
        <h3 className={shared.sectionTitle}>
          {m.route_lc_new_sources_heading()}{' '}
          <span className={shared.countBadge}>· {sourceArr.fields.length}</span>
        </h3>
        <div className={shared.page}>
          {sourceArr.fields.map((f, i) => (
            <div key={f.id} className={shared.formRow}>
              <Controller
                control={control}
                name={`source_entries.${i}.id` as const}
                render={({ field, fieldState }) => (
                  <StockEntryPicker
                    label={m.route_lc_new_source_item_label({ index: i + 1 })}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={m.route_lc_new_source_item_placeholder()}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                leadingIcon={<Trash2 size={14} />}
                disabled={sourceArr.fields.length <= 1}
                onClick={() => sourceArr.remove(i)}
                aria-label={m.route_lc_new_aria_remove_source()}
              />
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            leadingIcon={<Plus size={14} />}
            onClick={() => sourceArr.append({ id: '' })}
          >
            {m.route_lc_new_btn_add_source()}
          </Button>
        </div>
      </section>

      <section>
        {/* FE-119 Sprint Q+: section heading с count badge. */}
        <h3 className={shared.sectionTitle}>
          {m.route_lc_new_charges_heading()}{' '}
          <span className={shared.countBadge}>· {chargesArr.fields.length}</span>
        </h3>
        <div className={shared.page}>
          {chargesArr.fields.map((f, i) => (
            <div key={f.id} className={shared.filters}>
              {/* FE-117 Sprint Q+: required `*` markers. */}
              <Input
                label={m.route_lc_new_charge_title_label()}
                required
                {...register(`charges.${i}.title` as const)}
                placeholder={m.route_lc_new_charge_title_placeholder()}
                error={errors.charges?.[i]?.title?.message}
              />
              <Input
                label={m.route_lc_new_charge_amount_label()}
                required
                inputMode="decimal"
                {...register(`charges.${i}.amount` as const)}
                error={errors.charges?.[i]?.amount?.message}
              />
              <Controller
                control={control}
                name={`charges.${i}.currency` as const}
                render={({ field }) => (
                  <Select
                    label={m.route_lc_new_charge_currency_label()}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    options={[
                      { value: 'KZT', label: 'KZT' },
                      { value: 'USD', label: 'USD' },
                      { value: 'EUR', label: 'EUR' },
                      { value: 'RUB', label: 'RUB' },
                      { value: 'CNY', label: 'CNY' },
                    ]}
                  />
                )}
              />
              <Controller
                control={control}
                name={`charges.${i}.basis` as const}
                render={({ field }) => (
                  <Select
                    label={m.route_lc_new_charge_basis_label()}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value as LandedCostBasis)}
                    options={[
                      { value: 'by_qty', label: m.route_lc_new_basis_label({ basis: 'by_qty' }) },
                      {
                        value: 'by_amount',
                        label: m.route_lc_new_basis_label({ basis: 'by_amount' }),
                      },
                      {
                        value: 'by_weight',
                        label: m.route_lc_new_basis_label({ basis: 'by_weight' }),
                      },
                    ]}
                  />
                )}
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                leadingIcon={<Trash2 size={14} />}
                disabled={chargesArr.fields.length <= 1}
                onClick={() => chargesArr.remove(i)}
                aria-label={m.route_lc_new_aria_remove_charge()}
              />
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            leadingIcon={<Plus size={14} />}
            onClick={() =>
              chargesArr.append({ title: '', amount: '0', currency: 'KZT', basis: 'by_amount' })
            }
          >
            {m.route_lc_new_btn_add_charge()}
          </Button>
        </div>
      </section>

      <Input label={m.route_lc_new_notes_label()} {...register('notes')} />

      <div className={`${shared.formRow} ${shared.stickyFooter}`}>
        <Button
          type="button"
          variant="ghost"
          leadingIcon={<ArrowLeft size={14} />}
          onClick={() => nav({ to: '/landed-cost' })}
        >
          {m.common_button_cancel()}
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={!isValid || createMut.isPending}
          loading={createMut.isPending}
        >
          {m.route_lc_new_btn_create()}
        </Button>
      </div>
    </form>
  );
}
